import ToastContext from "context/toast-context";
import { useContext, useEffect, useState } from "react";
import Spinner from "./spinner";
import { uploadDocument } from "@snap-mobile/vault";
import DragAndDropVgsSide from "./drag-drop-vgs-side";
import { userFacingNameString } from "pages/application-status-v2/account-status-display-name";
import { SnapIcon } from "suit";

type DragAndDropVgsProps = {
  documentName: string;
  applicationId: string;
  vaultId: string;
  onUpload: () => void;
  unitDocumentId?: string;
};

function DragAndDropVgs({
  documentName,
  vaultId,
  applicationId,
  onUpload,
  unitDocumentId,
}: DragAndDropVgsProps) {
  const toast = useContext(ToastContext);
  const [isUploading, setIsUploading] = useState(false);
  const [documentFront, setDocumentFront] = useState<string>();
  const [documentBack, setDocumentBack] = useState<string>();
  const [fileType, setFileType] = useState<string>();
  const backRequired = documentName === "id_document";

  useEffect(() => {
    if (
      !isUploading &&
      documentFront &&
      ((backRequired && documentBack) || !backRequired) &&
      fileType
    ) {
      setIsUploading(true);
      uploadDocument({
        gateway: "unit",
        documentFront,
        documentBack,
        type: documentName,
        fileType,
        vaultId,
        unitApplicationId: applicationId,
        unitDocumentId,
      })
        .then(() => {
          toast?.setToast({
            message: "Successfully uploaded file",
            type: "success",
          });
          onUpload();
        })
        .catch(() => {
          toast?.setToast({
            message: "There was an error while uploading the file",
            type: "danger",
          });
        })
        .finally(() => {
          setDocumentFront(undefined);
          setDocumentBack(undefined);
          setIsUploading(false);
        });
    }
  }, [
    applicationId,
    backRequired,
    documentBack,
    documentFront,
    documentName,
    fileType,
    isUploading,
    onUpload,
    toast,
    unitDocumentId,
    vaultId,
  ]);

  return (
    <>
      <p className="my-1">{userFacingNameString(documentName)}</p>{" "}
      <div
        className={`flex flex-col lg:flex-row relative justify-center align-middle h-full w-full gap-5`}
      >
        <div className={`flex flex-col bg-blue-50 w-full rounded-lg`}>
          {backRequired && (
            <p className="my-1 text-center font-bold">
              Document Front{" "}
              <SnapIcon
                icon={
                  documentFront ? "check-circle-solid" : "circle-empty-solid"
                }
                color={documentFront ? "green" : "grey"}
                className="align-top"
              />
            </p>
          )}
          <DragAndDropVgsSide
            onFileChange={setDocumentFront}
            onFileTypeChange={setFileType}
          />
        </div>
        {backRequired && (
          <div className={`flex flex-col bg-blue-50 w-full rounded-lg`}>
            <p className="my-1 text-center font-bold">
              Document Back{" "}
              <SnapIcon
                icon={
                  documentBack ? "check-circle-solid" : "circle-empty-solid"
                }
                color={documentBack ? "green" : "grey"}
                className="align-top"
              />
            </p>
            <DragAndDropVgsSide onFileChange={setDocumentBack} />
          </div>
        )}
        {isUploading && (
          <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-10">
            <Spinner size="medium" className="mt-[5%]" />
          </div>
        )}
      </div>
    </>
  );
}
export default DragAndDropVgs;
